import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, KeyRound, Link2, Loader2, Plus, RefreshCw, ShieldAlert, Trash2, Webhook } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import api, { formatApiError } from '@/lib/api';

const copyText = async (value, label) => {
  await navigator.clipboard.writeText(value || '');
  toast.success(`${label} copied`);
};

export default function ApiKeysPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [error, setError] = useState('');

  const publicBaseUrl = useMemo(() => `${window.location.origin}/api/ledgerly`, []);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/ledgerly/admin/status');
      setStatus(data);
      const currentWebhook = data.webhooks?.find(item => item.is_active) || data.webhooks?.[0];
      if (currentWebhook && !webhookUrl) setWebhookUrl(currentWebhook.url || '');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [webhookUrl]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const createKey = async () => {
    setCreating(true);
    setError('');
    try {
      const { data } = await api.post('/ledgerly/admin/keys', { name: 'Ledgerly API Key', environment: 'production', rate_limit_per_minute: 60 });
      setNewKey(data);
      toast.success('Ledgerly API key created');
      await loadStatus();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm('Revoke this Ledgerly API key? Ledgerly will stop syncing with this key.')) return;
    await api.delete(`/ledgerly/admin/keys/${keyId}`);
    toast.success('Ledgerly API key revoked');
    await loadStatus();
  };

  const saveWebhook = async () => {
    setSavingWebhook(true);
    setError('');
    try {
      const { data } = await api.post('/ledgerly/admin/webhooks', {
        name: 'Ledgerly Webhook',
        url: webhookUrl,
        webhook_secret: webhookSecret || undefined,
        events: status?.supported_events || [],
        environment: 'production',
      });
      toast.success('Ledgerly webhook saved');
      setWebhookSecret('');
      setStatus(prev => ({ ...prev, webhooks: [data, ...(prev?.webhooks || [])] }));
      await loadStatus();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingWebhook(false);
    }
  };

  const testWebhook = async (webhookId) => {
    await api.post(`/ledgerly/admin/webhooks/${webhookId}/test`);
    toast.success('Test webhook queued');
    await loadStatus();
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">Admin only</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-zinc-950 font-heading" data-testid="api-keys-title">API Keys & Integrations</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-500">Copy Ledgerly connection details, rotate sync keys, and configure webhook delivery.</p>
        </div>
        <Button variant="outline" onClick={loadStatus} disabled={loading} data-testid="api-keys-refresh-button">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
        </Button>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="api-keys-error-alert">{error}</div>}

      <Card className="border-2 border-red-200 bg-red-50/60" data-testid="ledgerly-warning-card">
        <CardContent className="flex gap-3 p-4 text-sm text-red-800">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-black">Keep API keys and webhook secrets private.</p>
            <p>Only share these details with Ledgerly or trusted integration partners. Revoke keys immediately if one is exposed.</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <InfoCard icon={Link2} title="Base URL" value={publicBaseUrl} testId="ledgerly-base-url" onCopy={() => copyText(publicBaseUrl, 'Base URL')} />
        <InfoCard icon={KeyRound} title="Auth" value="Bearer token or X-API-Key" testId="ledgerly-auth-style" onCopy={() => copyText('Authorization: Bearer <key>', 'Auth header')} />
        <InfoCard icon={Webhook} title="OpenAPI" value={`${publicBaseUrl}/openapi`} testId="ledgerly-openapi-url" onCopy={() => copyText(`${publicBaseUrl}/openapi`, 'OpenAPI URL')} />
      </div>

      <Card data-testid="ledgerly-keys-card">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" /> Ledgerly API Keys</CardTitle>
          <Button onClick={createKey} disabled={creating} data-testid="create-ledgerly-key-button">
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Create New Key
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {newKey && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="new-ledgerly-key-alert">
              <p className="text-sm font-black text-amber-900">Copy this key now — it will only be shown once.</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Input readOnly value={newKey.api_key} className="font-mono text-xs" data-testid="new-ledgerly-key-value" />
                <Button variant="outline" onClick={() => copyText(newKey.api_key, 'New Ledgerly API key')} data-testid="copy-new-ledgerly-key-button"><Copy className="mr-2 h-4 w-4" /> Copy</Button>
              </div>
            </div>
          )}
          {(status?.keys || []).map(key => (
            <div key={key.id} className="flex flex-col gap-3 rounded-md border border-zinc-200 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`ledgerly-key-row-${key.id}`}>
              <div>
                <div className="flex flex-wrap items-center gap-2"><p className="font-bold text-zinc-950">{key.name}</p><Badge className={key.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'} data-testid={`ledgerly-key-status-${key.id}`}>{key.is_active ? 'Active' : 'Revoked'}</Badge><Badge variant="secondary">{key.environment}</Badge></div>
                <p className="mt-1 font-mono text-xs text-zinc-500" data-testid={`ledgerly-key-masked-${key.id}`}>{key.key_masked}</p>
                <p className="text-xs text-zinc-400">Rate limit {key.rate_limit_per_minute}/min · Last used {key.last_used_at || 'never'}</p>
              </div>
              {key.is_active && <Button variant="outline" size="sm" onClick={() => revokeKey(key.id)} data-testid={`revoke-ledgerly-key-${key.id}`}><Trash2 className="mr-2 h-4 w-4" /> Revoke</Button>}
            </div>
          ))}
          {status?.keys?.length === 0 && <p className="rounded-md bg-zinc-50 p-3 text-sm text-zinc-500" data-testid="ledgerly-no-keys">No Ledgerly keys yet.</p>}
        </CardContent>
      </Card>

      <Card data-testid="ledgerly-webhooks-card">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Webhook className="h-4 w-4" /> Ledgerly Webhooks</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[1fr_260px_auto]">
            <div><Label>Ledgerly webhook URL</Label><Input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://app.ledgerly.com/api/webhooks/crm" data-testid="ledgerly-webhook-url-input" /></div>
            <div><Label>Webhook secret</Label><Input value={webhookSecret} onChange={e => setWebhookSecret(e.target.value)} placeholder="Optional — generated if blank" data-testid="ledgerly-webhook-secret-input" /></div>
            <Button className="self-end" onClick={saveWebhook} disabled={savingWebhook || !webhookUrl} data-testid="save-ledgerly-webhook-button">{savingWebhook ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Save</Button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="ledgerly-events-list">{(status?.supported_events || []).map(event => <Badge key={event} variant="secondary">{event}</Badge>)}</div>
          {(status?.webhooks || []).map(webhook => (
            <div key={webhook.id} className="rounded-md border border-zinc-200 p-3" data-testid={`ledgerly-webhook-row-${webhook.id}`}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"><div><p className="font-bold text-zinc-950">{webhook.name}</p><p className="break-all text-xs text-zinc-500">{webhook.url || 'No URL configured'}</p><p className="font-mono text-xs text-zinc-400">{webhook.webhook_secret_masked}</p></div><Button variant="outline" size="sm" disabled={!webhook.url} onClick={() => testWebhook(webhook.id)} data-testid={`test-ledgerly-webhook-${webhook.id}`}>Test</Button></div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function InfoCard({ icon: Icon, title, value, onCopy, testId }) {
  return <Card data-testid={`${testId}-card`}><CardContent className="p-4"><div className="flex items-center justify-between gap-3"><div className="min-w-0"><p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400"><Icon className="h-3.5 w-3.5" /> {title}</p><p className="mt-2 truncate font-mono text-sm font-semibold text-zinc-900" data-testid={testId}>{value}</p></div><Button variant="outline" size="sm" onClick={onCopy} data-testid={`copy-${testId}-button`}><Copy className="h-4 w-4" /></Button></div></CardContent></Card>;
}